/**
 * Small formulas written in the tools: the chance a spell freezes something,
 * how often a hit lands, anything that is a number worked out from levels.
 *
 * A language of its own rather than JavaScript, and read by a parser here
 * rather than handed to anything that runs code: a formula is written by
 * whoever has the tools, run by the server on every cast, and must never be a
 * way to make the server do something else. It knows numbers, the names below,
 * arithmetic, comparisons and a short list of functions, and nothing more.
 */

/** Every name a formula may use, and what it means, for the tools to offer. */
export const FORMULA_NAMES: Readonly<Record<string, string>> = {
  magic: "the caster's Magic level",
  ranged: "the caster's Ranged level",
  attack: "the caster's Attack level",
  strength: "the caster's Strength level",
  defence: "the caster's Defence level",
  constitution: "the caster's Constitution level",
  faith: "the caster's Faith level",
  combat: "the caster's combat level",
  magicBonus: "the caster's magic attack bonus from what they wear",
  magicDamage: "the caster's magic damage bonus from what they wear",
  targetLevel: "the target's combat level",
  targetDefence: "the target's Defence level",
  targetMagic: "the target's Magic level",
  targetMagicDefence: "the target's defence against magic from what they wear",
  targetHealth: "the target's health now",
  targetMaxHealth: "the target's most health",
  hit: 'one if the spell landed, nought if it missed',
  damage: 'the damage the spell did',
};

/** And every function, with how many numbers it takes. */
const FUNCTIONS: Readonly<
  Record<string, { min: number; max: number; run: (...args: number[]) => number }>
> = {
  min: { min: 1, max: 8, run: (...args) => Math.min(...args) },
  max: { min: 1, max: 8, run: (...args) => Math.max(...args) },
  clamp: { min: 3, max: 3, run: (x, lo, hi) => Math.min(Math.max(x ?? 0, lo ?? 0), hi ?? 0) },
  floor: { min: 1, max: 1, run: (x) => Math.floor(x ?? 0) },
  ceil: { min: 1, max: 1, run: (x) => Math.ceil(x ?? 0) },
  round: { min: 1, max: 1, run: (x) => Math.round(x ?? 0) },
  abs: { min: 1, max: 1, run: (x) => Math.abs(x ?? 0) },
  sqrt: { min: 1, max: 1, run: (x) => Math.sqrt(Math.max(0, x ?? 0)) },
  pow: { min: 2, max: 2, run: (x, y) => Math.pow(x ?? 0, y ?? 0) },
  log: { min: 1, max: 1, run: (x) => Math.log(Math.max(1e-9, x ?? 0)) },
  random: { min: 0, max: 0, run: () => Math.random() },
};

/** The names of the functions, for the tools to offer. */
export const FORMULA_FUNCTIONS: readonly string[] = Object.keys(FUNCTIONS);

type Node =
  | { kind: 'number'; value: number }
  | { kind: 'name'; name: string }
  | { kind: 'call'; name: string; args: Node[] }
  | { kind: 'unary'; op: '-' | '!'; value: Node }
  | { kind: 'binary'; op: string; left: Node; right: Node };

/** What went wrong reading one, said so the author can fix it. */
export class FormulaError extends Error {}

function tokens(text: string): string[] {
  const found: string[] = [];
  const pattern =
    /\s*(\d+(?:\.\d+)?|\.\d+|[A-Za-z_][A-Za-z0-9_]*|<=|>=|==|!=|&&|\|\||[-+*/%^(),<>!?:])/y;
  let at = 0;
  while (at < text.length) {
    if (/^\s*$/.test(text.slice(at))) break;
    pattern.lastIndex = at;
    const match = pattern.exec(text);
    if (!match?.[1]) throw new FormulaError(`Cannot read "${text.slice(at).trim().slice(0, 12)}"`);
    found.push(match[1]);
    at = pattern.lastIndex;
  }
  return found;
}

/** Reads a formula into something that can be worked out, or says what is wrong with it. */
export function parseFormula(text: string): Node {
  if (text.length > 400) throw new FormulaError('That formula is too long.');
  const list = tokens(text);
  if (list.length === 0) throw new FormulaError('The formula is empty.');
  let at = 0;
  const peek = (): string | undefined => list[at];
  const take = (wanted?: string): string => {
    const held = list[at];
    if (held === undefined) throw new FormulaError('The formula stops too soon.');
    if (wanted !== undefined && held !== wanted)
      throw new FormulaError(`Expected "${wanted}" but found "${held}".`);
    at++;
    return held;
  };

  // Lowest binding first: a ? b : c, then ||, &&, comparisons, + -, * / %, ^.
  const conditional = (): Node => {
    const test = or();
    if (peek() !== '?') return test;
    take('?');
    const yes = conditional();
    take(':');
    const no = conditional();
    return { kind: 'call', name: '?', args: [test, yes, no] };
  };
  const chain = (next: () => Node, ops: string[]) => (): Node => {
    let left = next();
    while (ops.includes(peek() ?? '')) {
      const op = take();
      left = { kind: 'binary', op, left, right: next() };
    }
    return left;
  };
  const power = (): Node => {
    const base = unary();
    if (peek() !== '^') return base;
    take('^');
    return { kind: 'binary', op: '^', left: base, right: power() };
  };
  const unary = (): Node => {
    if (peek() === '-' || peek() === '!') {
      const op = take() as '-' | '!';
      return { kind: 'unary', op, value: unary() };
    }
    if (peek() === '+') {
      take();
      return unary();
    }
    return primary();
  };
  const primary = (): Node => {
    const held = take();
    if (/^[\d.]/.test(held)) return { kind: 'number', value: Number(held) };
    if (held === '(') {
      const inner = conditional();
      take(')');
      return inner;
    }
    if (/^[A-Za-z_]/.test(held)) {
      if (peek() === '(') {
        // The table's own entries only. Asked with a plain lookup, a name every
        // object inherits, constructor say, would be found and run.
        const fn = Object.hasOwn(FUNCTIONS, held) ? FUNCTIONS[held] : undefined;
        if (!fn) throw new FormulaError(`There is no function called ${held}.`);
        take('(');
        const args: Node[] = [];
        if (peek() !== ')') {
          args.push(conditional());
          while (peek() === ',') {
            take(',');
            args.push(conditional());
          }
        }
        take(')');
        if (args.length < fn.min || args.length > fn.max) {
          throw new FormulaError(
            `${held} takes ${fn.min === fn.max ? fn.min : `${fn.min} to ${fn.max}`} numbers.`,
          );
        }
        return { kind: 'call', name: held, args };
      }
      if (!Object.hasOwn(FORMULA_NAMES, held))
        throw new FormulaError(`There is nothing called ${held}.`);
      return { kind: 'name', name: held };
    }
    throw new FormulaError(`Did not expect "${held}".`);
  };
  const product = chain(power, ['*', '/', '%']);
  const sum = chain(product, ['+', '-']);
  const compare = chain(sum, ['<', '>', '<=', '>=', '==', '!=']);
  const and = chain(compare, ['&&']);
  const or = chain(and, ['||']);

  const tree = conditional();
  if (at < list.length) throw new FormulaError(`Did not expect "${list[at]}".`);
  return tree;
}

function run(node: Node, values: Readonly<Record<string, number>>): number {
  switch (node.kind) {
    case 'number':
      return node.value;
    case 'name':
      return Object.hasOwn(values, node.name) ? (values[node.name] ?? 0) : 0;
    case 'unary': {
      const held = run(node.value, values);
      return node.op === '-' ? -held : held === 0 ? 1 : 0;
    }
    case 'call': {
      if (node.name === '?') {
        return run(node.args[0] as Node, values) !== 0
          ? run(node.args[1] as Node, values)
          : run(node.args[2] as Node, values);
      }
      const fn = Object.hasOwn(FUNCTIONS, node.name) ? FUNCTIONS[node.name] : undefined;
      return fn ? fn.run(...node.args.map((arg) => run(arg, values))) : 0;
    }
    case 'binary': {
      const a = run(node.left, values);
      const b = run(node.right, values);
      switch (node.op) {
        case '+':
          return a + b;
        case '-':
          return a - b;
        case '*':
          return a * b;
        case '/':
          return b === 0 ? 0 : a / b;
        case '%':
          return b === 0 ? 0 : a % b;
        case '^':
          return Math.pow(a, b);
        case '<':
          return a < b ? 1 : 0;
        case '>':
          return a > b ? 1 : 0;
        case '<=':
          return a <= b ? 1 : 0;
        case '>=':
          return a >= b ? 1 : 0;
        case '==':
          return a === b ? 1 : 0;
        case '!=':
          return a !== b ? 1 : 0;
        case '&&':
          return a !== 0 && b !== 0 ? 1 : 0;
        case '||':
          return a !== 0 || b !== 0 ? 1 : 0;
        default:
          return 0;
      }
    }
  }
}

/** Works a formula out. A formula that cannot be read comes to nothing rather than stopping anything. */
export function evaluateFormula(text: string, values: Readonly<Record<string, number>>): number {
  try {
    const held = run(parseFormula(text), values);
    return Number.isFinite(held) ? held : 0;
  } catch {
    return 0;
  }
}

/** Whether a formula can be read, or what is wrong with it. */
export function checkFormula(text: string): string | null {
  try {
    parseFormula(text);
    return null;
  } catch (error) {
    return error instanceof FormulaError ? error.message : 'That cannot be read.';
  }
}

/**
 * Chances worth having ready, for the tools to offer before anybody writes one.
 *
 * Each comes to a number between nought and one, which is how a chance is read.
 */
export const CHANCE_PRESETS: readonly { label: string; formula: string }[] = [
  { label: 'Always', formula: '1' },
  { label: 'Whenever the spell lands', formula: 'hit' },
  { label: 'Half the time it lands', formula: 'hit * 0.5' },
  {
    label: 'Magic against their Defence',
    formula: 'hit * clamp(0.5 + (magic - targetDefence) / 60, 0.1, 0.95)',
  },
  {
    label: 'Magic against their Magic',
    formula: 'hit * clamp(0.5 + (magic - targetMagic) / 60, 0.1, 0.95)',
  },
  { label: 'Magic level out of 99', formula: 'hit * magic / 99' },
  {
    label: 'Less often against stronger targets',
    formula: 'hit * clamp(1 - targetLevel / (combat + targetLevel), 0.1, 0.9)',
  },
];
